import { Component, Inject, OnInit, OnDestroy, signal, HostListener } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { AppService } from '@app/app.service';
import { MatDialogRef } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { CommonModule } from '@angular/common';
import { CurrencyPipe } from '@angular/common';
import { DatePipe } from '@angular/common';
import { ClipboardModule, Clipboard } from '@angular/cdk/clipboard';
import { ShowEmailDialogComponent } from '@app/shared/show-email-dialog/show-email-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatChipsModule } from '@angular/material/chips';
import { MatExpansionModule } from '@angular/material/expansion';

@Component({
  selector: 'app-load-view-public-dialog',
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatTooltipModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatCardModule,
    CommonModule,
    CurrencyPipe,
    DatePipe,
    ClipboardModule,
    MatChipsModule,
    MatExpansionModule,
  ],
  templateUrl: './load-view-public-dialog.component.html',
  styleUrl: './load-view-public-dialog.component.scss',
})
export class LoadViewPublicDialogComponent implements OnInit, OnDestroy {
  loadId: number | null = null;
  load = signal<any>(null);
  loading = signal(false);

  isMobile = window.innerWidth < 768;

  @HostListener('window:resize')
  onResize(): void {
    this.isMobile = window.innerWidth < 768;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private appService: AppService,
    private dialogRef: MatDialogRef<LoadViewPublicDialogComponent>,
    private clipboard: Clipboard,
    private dialog: MatDialog
  ) {
    // Assign passed data to the load signal.
    // This allows the dialog to display initial data immediately if available.
    // this.load.set(data?.load ?? null);
  }

  ngOnInit(): void {
    this.loadId = this.data?.load?.id;

    if (!this.loadId) {
      return;
    }

    this.getLoad();

  }

  ngOnDestroy(): void {
    // Clean up any resources or subscriptions if necessary.
  }

  getLoad(): any {
    this.loading.set(true);

    this.appService.get(`loads/view/${this.loadId}`).subscribe({
      next: (response: any) => {
        this.load.set(response.data);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
      },
    });
  }

  openInNewTab(): void {
    window.open(
      `${window.location.origin}/loads/view/${this.loadId}`,
      '_blank'
    );

    this.dialogRef.close();
  }

  toggleSavedLoad(): void {

    const originalValue = this.load().is_saved;

    this.load.update(current => ({
      ...current,
      is_saved: !current.is_saved,
    }));
    // Trigger the parent component to update its saved loads list
    this.data.toggleSavedLoad();

  }

  formatPhone(phone: string | null | undefined): string {
    if (!phone) {
      return '';
    }

    const digits = phone.replace(/\D/g, '');

    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }

    return phone;
  }

  getDimensions(load: any): string {
    const length = this.toFeetAndInches(load.length);
    const width = this.toFeetAndInches(load.width);
    const height = this.toFeetAndInches(load.height);

    const dimensions = [length, width, height]
      .filter((dim) => dim)
      .join(' × ');

    return dimensions;
  }

  toFeetAndInches(totalInches: number): string {
    const feet = Math.floor(totalInches / 12);
    const inches = totalInches % 12;

    return `${feet}-${inches}`;
  }

  getCarrierType(carrier: any): string {
    const isCarrier = carrier.is_carrier ?? false;
    const isBroker = carrier.is_broker ?? false;

    if (isBroker && isCarrier) {
      return 'BROKER & CARRIER';
    }

    if (isBroker) {
      return 'BROKER';
    }

    if (isCarrier) {
      return 'CARRIER';
    }

    return '';
  }

  copyLink(): void {
    const url = `${window.location.origin}/loads/view/${this.loadId}`;
    this.clipboard.copy(url);
    this.appService.showSnackBar('Link copied to clipboard.', 'success');
  }

  openGoogleMaps(): void {
    const origin = `${this.load().origin_city}, ${this.load().origin_state}`;
    const destination = `${this.load().destination_city}, ${this.load().destination_state}`;

    const mapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(
      origin
    )}&destination=${encodeURIComponent(destination)}`;
    window.open(mapsUrl, '_blank');
  }

  openMotus(): void {
    const USDOTNumber = this.load().carrier?.dot_number;
    const url = `https://motus.dot.gov/customer/${USDOTNumber}/account`;
    window.open(url, '_blank');
  }

  showUserEmail(): void {
    this.dialog.open(ShowEmailDialogComponent, {
      width: '400px',
      data: {
        url: `loads/view/${this.loadId}/email`,
        load: this.load(),
      },
    });
  }

  get tags(): string[] {
    const load = this.load();

    if (!load) {
      return [];
    }

    const tags: string[] = [];

    if (!load.operable) {
      tags.push('Non-Operable');
    }

    const tagMap: Record<string, string> = {
      tarp: 'Tarp',
      hazmat: 'Hazmat',
      military_base_access: 'Military Base Access',
      twic: 'TWIC',
      emodal: 'eModal',
      outriggers: 'Outriggers',
      divisible: 'Divisible',
      beam_trailer: 'Beam Trailer',
      extendable_trailer: 'Extendable Trailer',
      permits_reimbursed: 'Permits Reimbursed',
    };

    Object.entries(tagMap).forEach(([key, label]) => {
      if (load[key]) {
        tags.push(label);
      }
    });

    return tags;
  }
}